"use client";

import { useEffect, useId, useRef, useState } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useComponentsTranslations, useTasksTranslations } from "@/hooks/use-translations";

type ReminderUnit = "minutes" | "hours" | "days" | "weeks";

const PRESET_REMINDER_OPTIONS = [
  { value: "none", minutes: null as number | null, labelKey: "no_reminder" },
  { value: "preset:0", minutes: 0, labelKey: "reminder_at_task_time" },
  { value: "preset:5", minutes: 5, labelKey: "reminder_5_minutes" },
  { value: "preset:10", minutes: 10, labelKey: "reminder_10_minutes" },
  { value: "preset:15", minutes: 15, labelKey: "reminder_15_minutes" },
  { value: "preset:30", minutes: 30, labelKey: "reminder_30_minutes" },
  { value: "preset:60", minutes: 60, labelKey: "reminder_1_hour_before" },
  { value: "preset:1440", minutes: 1440, labelKey: "reminder_1_day_before" },
  { value: "custom", minutes: -1, labelKey: "custom_reminder" },
] as const;

const UNIT_TO_MINUTES: Record<ReminderUnit, number> = {
  minutes: 1,
  hours: 60,
  days: 1440,
  weeks: 10080,
};

const MAX_CUSTOM_REMINDER_MINUTES = Math.max(
  30 * UNIT_TO_MINUTES.days,
  4 * UNIT_TO_MINUTES.weeks,
  23 * UNIT_TO_MINUTES.hours,
);

const UNIT_LIMITS: Record<ReminderUnit, number> = {
  minutes: MAX_CUSTOM_REMINDER_MINUTES,
  hours: 23,
  days: 30,
  weeks: 4,
};

type TaskReminderSelectProps = {
  value: number | null;
  onChange: (value: number | null) => void;
  id?: string;
  showHelpText?: boolean;
};

export function TaskReminderSelect({
  value,
  onChange,
  id,
  showHelpText = true,
}: TaskReminderSelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const tComponents = useComponentsTranslations();
  const tTasks = useTasksTranslations();
  const [customAmount, setCustomAmount] = useState("1");
  const [customUnit, setCustomUnit] = useState<ReminderUnit>("days");
  const [selectedOption, setSelectedOption] = useState<string>(() => {
    if (value === null) {
      return "none";
    }

    const presetOption = PRESET_REMINDER_OPTIONS.find((option) => option.minutes === value);
    return presetOption?.value ?? "custom";
  });
  const lastCommittedValueRef = useRef<number | null | undefined>(value);

  useEffect(() => {
    if (value === null || value === 0) {
      return;
    }

    if (value % UNIT_TO_MINUTES.weeks === 0 && value / UNIT_TO_MINUTES.weeks <= UNIT_LIMITS.weeks) {
      setCustomAmount(String(value / UNIT_TO_MINUTES.weeks));
      setCustomUnit("weeks");
      return;
    }

    if (value % UNIT_TO_MINUTES.days === 0 && value / UNIT_TO_MINUTES.days <= UNIT_LIMITS.days) {
      setCustomAmount(String(value / UNIT_TO_MINUTES.days));
      setCustomUnit("days");
      return;
    }

    if (value % UNIT_TO_MINUTES.hours === 0 && value / UNIT_TO_MINUTES.hours <= UNIT_LIMITS.hours) {
      setCustomAmount(String(value / UNIT_TO_MINUTES.hours));
      setCustomUnit("hours");
      return;
    }

    setCustomAmount(String(Math.max(1, Math.min(value, UNIT_LIMITS.minutes))));
    setCustomUnit("minutes");
  }, [value]);

  useEffect(() => {
    if (value === lastCommittedValueRef.current) {
      return;
    }

    lastCommittedValueRef.current = value;

    if (value === null) {
      setSelectedOption("none");
      return;
    }

    const presetOption = PRESET_REMINDER_OPTIONS.find((option) => option.minutes === value);
    setSelectedOption(presetOption?.value ?? "custom");
  }, [value]);

  const applyCustomReminder = (amountValue: string, unitValue: ReminderUnit) => {
    const parsedAmount = Number.parseInt(amountValue, 10);
    const safeAmount = Math.max(1, Math.min(Number.isFinite(parsedAmount) ? parsedAmount : 1, UNIT_LIMITS[unitValue]));
    const nextValue = safeAmount * UNIT_TO_MINUTES[unitValue];
    setCustomAmount(String(safeAmount));
    setSelectedOption("custom");
    lastCommittedValueRef.current = nextValue;
    onChange(nextValue);
  };

  return (
    <div className="grid gap-2">
      <Label htmlFor={selectId}>{tComponents("reminder")}</Label>
      <Select
        value={selectedOption}
        onValueChange={(nextValue) => {
          setSelectedOption(nextValue);

          if (nextValue === "none") {
            lastCommittedValueRef.current = null;
            onChange(null);
            return;
          }

          if (nextValue === "custom") {
            applyCustomReminder(customAmount, customUnit);
            return;
          }

          const presetMinutes = PRESET_REMINDER_OPTIONS.find((option) => option.value === nextValue)?.minutes;
          lastCommittedValueRef.current = presetMinutes ?? null;
          onChange(presetMinutes ?? null);
        }}
      >
        <SelectTrigger id={selectId}>
          <SelectValue placeholder={tComponents("no_reminder")} />
        </SelectTrigger>
        <SelectContent>
          {PRESET_REMINDER_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {tComponents(option.labelKey)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selectedOption === "custom" ? (
        <div className="grid gap-3 rounded-lg border border-border/60 bg-muted/30 p-3 sm:grid-cols-[120px_minmax(0,1fr)]">
          <div className="grid gap-2">
            <Label htmlFor={`${selectId}-amount`}>{tComponents("reminder_value")}</Label>
            <Input
              id={`${selectId}-amount`}
              type="number"
              min={1}
              max={UNIT_LIMITS[customUnit]}
              value={customAmount}
              onChange={(event) => {
                setCustomAmount(event.target.value);
                if (event.target.value !== "" && Number.isFinite(Number.parseInt(event.target.value, 10))) {
                  applyCustomReminder(event.target.value, customUnit);
                }
              }}
              onBlur={() => applyCustomReminder(customAmount, customUnit)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`${selectId}-unit`}>{tComponents("reminder_unit")}</Label>
            <Select
              value={customUnit}
              onValueChange={(nextUnit) => {
                const parsedUnit = nextUnit as ReminderUnit;
                setCustomUnit(parsedUnit);
                applyCustomReminder(customAmount, parsedUnit);
              }}
            >
              <SelectTrigger id={`${selectId}-unit`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minutes">{tTasks("minutes")}</SelectItem>
                <SelectItem value="hours">{tComponents("hours")}</SelectItem>
                <SelectItem value="days">{tComponents("days")}</SelectItem>
                <SelectItem value="weeks">{tComponents("weeks")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : null}
      {showHelpText ? (
        <p className="text-sm text-muted-foreground">{tComponents("reminder_help")}</p>
      ) : null}
    </div>
  );
}
